/**
 * Polls GET /api/playlists/smart/:id while Ella Radio stage-2 builds in the background.
 * Reconciles the full server track list each successful poll; retries failed fetches indefinitely.
 */

import { smartPlaylistService } from '../services/smartPlaylistService';
import { QueueController } from '../transport/QueueController';
import {
  ellaApiTrackToSessionTrack,
  ellaDetailTrackToApiTrack,
  ellaTracksToQueueFormat,
  enrichEllaApiTracksWithCoverArt,
  type EllaApiTrack,
} from './ellaQueueFormat';
import {
  isRadioTrackPendingEnqueue,
  reconcileRadioTrackRegistryFromServer,
  seedRadioTrackRegistry,
  syncRadioTrackRegistryFromQueue,
} from './radioTrackRegistry';
import { isBuildHeartbeatStale } from './ellaRadioBuildHeartbeat';
import { diagnosticLog } from '../utils/diagnosticLogger';
import {
  handleBuildStallCheck,
  markRadioBuildFatal,
  notifyRadioTracksAppended,
  resetRadioSessionPollFlags,
  setRadioSessionBuildMeta,
  armRadioConnectorHold,
  releaseRadioConnectorHoldIfIdle,
} from './ellaRadioSessionState';
import {
  startEllaRadioLowQueueWatch,
  stopEllaRadioLowQueueWatch,
} from './ellaRadioLowQueueWatch';

export type EllaBuildStage =
  | 'finding_opener'
  | 'shaping'
  | 'still_shaping'
  | 'connectivity'
  | 'ready'
  | 'partial';

export interface EllaBuildProgress {
  stage: EllaBuildStage;
  queuedCount: number;
  targetCount: number;
  startedAt: number;
  partialMessage?: string;
}

export interface EllaBuildPollCallbacks {
  onProgress: (progress: EllaBuildProgress) => void;
  onSessionTracksAppended: (sessionTracks: ReturnType<typeof ellaApiTrackToSessionTrack>[]) => void;
  onReady: () => void;
  onPartialFailure: (message: string) => void;
  onBuildFailed?: (message: string) => void;
  onConnectivityIssue?: () => void;
  onConnectivityRecovered?: () => void;
}

const POLL_INTERVAL_MS = 2000;
const POLL_BACKOFF_MS = [2000, 5000, 10_000, 15_000] as const;
/** Informational UX — emit still_shaping but keep polling. */
export const UX_PATIENCE_MS = 60_000;
export const BUILD_TOAST_MS = 10_000;

/** User-facing partial message when the server supplies buildError. */
export function formatPartialBuildMessage(
  queuedCount: number,
  targetCount: number,
  buildError?: unknown,
): string {
  const err = String(buildError || '').trim();
  if (err) {
    return `${queuedCount} tracks — we couldn't fill all ${targetCount}: ${err}`;
  }
  return 'Could not finish the full set — your station will continue with what\'s queued.';
}

let _pollTimer: ReturnType<typeof setTimeout> | null = null;
let _playlistId: string | null = null;
let _lastServerQueuedCount = 0;
let _lastServerTrackIncreaseAt = 0;
let _stillShapingEmitted = false;
let _connectivityActive = false;
let _pollBackoffIndex = 0;
let _startedAt = 0;
let _targetCount = 0;
let _callbacks: EllaBuildPollCallbacks | null = null;
let _tickInFlight = false;
let _wakeListenerInstalled = false;

function logRadioPoll(
  level: 'info' | 'warn' | 'error',
  message: string,
  payload?: Record<string, unknown>,
): void {
  diagnosticLog(level, 'js:radio', message, payload);
  const fn = level === 'error' ? console.error : console.info;
  fn(`[EllaRadioBuildPoll] ${message}`, payload ?? '');
}

function extractBuildMeta(detail: Record<string, unknown>) {
  const blueprint = (detail.playlist as { blueprint?: Record<string, unknown> } | undefined)?.blueprint;
  const buildStatus = String(detail.buildStatus || detail.status || 'ready');
  const buildLastProgressAt = String(
    detail.buildLastProgressAt || blueprint?.buildLastProgressAt || '',
  ) || null;
  const buildStartedAt = String(blueprint?.buildStartedAt || '') || null;
  return { buildStatus, buildLastProgressAt, buildStartedAt, blueprint };
}

function nextPollDelayMs(success: boolean): number {
  if (success) {
    _pollBackoffIndex = 0;
    return POLL_INTERVAL_MS;
  }
  const delay = POLL_BACKOFF_MS[Math.min(_pollBackoffIndex, POLL_BACKOFF_MS.length - 1)];
  if (_pollBackoffIndex < POLL_BACKOFF_MS.length - 1) _pollBackoffIndex += 1;
  return delay;
}

function schedulePoll(delayMs: number) {
  if (!_playlistId || !_callbacks) return;
  if (_pollTimer) clearTimeout(_pollTimer);
  _pollTimer = setTimeout(() => {
    void pollTick();
  }, delayMs);
}

/** Nudge an active poll loop after wake/focus — immediate reconcile + resume. */
export function resumeEllaRadioBuildPollOnWake(): void {
  if (!_playlistId || !_callbacks) return;
  logRadioPoll('info', 'wake_resume', { playlistId: _playlistId });
  schedulePoll(0);
}

function onDocumentWake() {
  if (typeof document === 'undefined' || document.visibilityState !== 'visible') return;
  resumeEllaRadioBuildPollOnWake();
}

function installEllaRadioBuildPollWakeListener(): void {
  if (_wakeListenerInstalled || typeof document === 'undefined') return;
  _wakeListenerInstalled = true;
  document.addEventListener('visibilitychange', onDocumentWake);
  window.addEventListener('focus', onDocumentWake);
}

function emitProgress(stage: EllaBuildStage, queuedCount: number, partialMessage?: string) {
  logRadioPoll('info', 'progress', { stage, queuedCount, targetCount: _targetCount, partialMessage });
  _callbacks?.onProgress({
    stage,
    queuedCount,
    targetCount: _targetCount,
    startedAt: _startedAt,
    partialMessage,
  });
}

function markConnectivityIssue() {
  if (!_connectivityActive) {
    _connectivityActive = true;
    logRadioPoll('warn', 'connectivity_issue');
    _callbacks?.onConnectivityIssue?.();
  }
  emitProgress('connectivity', _lastServerQueuedCount);
}

function markConnectivityRecovered() {
  if (!_connectivityActive) return;
  _connectivityActive = false;
  logRadioPoll('info', 'connectivity_recovered');
  _callbacks?.onConnectivityRecovered?.();
}

async function reconcileTracksFromServer(apiTracks: EllaApiTrack[]): Promise<number> {
  const serverIds = apiTracks.map((t) => String(t.trackId || '')).filter(Boolean);
  reconcileRadioTrackRegistryFromServer(serverIds);

  // Preserve server position order — single ordered append batch.
  const newApiTracks = apiTracks.filter(
    (t) => t.trackId && isRadioTrackPendingEnqueue(String(t.trackId)),
  );

  if (!newApiTracks.length) {
    logRadioPoll('info', 'reconcile_no_enqueue', {
      serverCount: apiTracks.length,
      registrySize: serverIds.length,
    });
    return 0;
  }

  const enrichedTracks = await enrichEllaApiTracksWithCoverArt(newApiTracks);
  QueueController.appendToQueue(ellaTracksToQueueFormat(enrichedTracks), 'ellaRadioBuildPoll');
  _callbacks?.onSessionTracksAppended(enrichedTracks.map(ellaApiTrackToSessionTrack));
  logRadioPoll('info', 'reconcile_enqueued', {
    appended: enrichedTracks.length,
    serverCount: apiTracks.length,
  });
  notifyRadioTracksAppended(enrichedTracks.length, 'ellaRadioBuildPoll');
  return enrichedTracks.length;
}

async function pollTick() {
  if (!_playlistId || !_callbacks || _tickInFlight) return;
  _tickInFlight = true;
  try {
    const detail = await smartPlaylistService.getDetail(_playlistId);
    if (!detail?.success) {
      logRadioPoll('warn', 'poll_unsuccessful', { playlistId: _playlistId });
      markConnectivityIssue();
      schedulePoll(nextPollDelayMs(false));
      return;
    }

    markConnectivityRecovered();

    const { buildStatus, buildLastProgressAt, buildStartedAt, blueprint } = extractBuildMeta(
      detail as Record<string, unknown>,
    );
    const apiTracks = (detail.tracks || []).map(ellaDetailTrackToApiTrack);
    const queuedCount = apiTracks.length;
    const nowMs = Date.now();

    const appended = await reconcileTracksFromServer(apiTracks);
    if (appended > 0 || queuedCount > _lastServerQueuedCount) {
      _lastServerTrackIncreaseAt = nowMs;
      _stillShapingEmitted = false;
    }
    _lastServerQueuedCount = queuedCount;

    if (_playlistId) {
      setRadioSessionBuildMeta(_playlistId, buildStatus, buildLastProgressAt, buildStartedAt);
    }

    logRadioPoll('info', 'poll_ok', {
      buildStatus,
      queuedCount,
      buildLastProgressAt,
      appended,
    });

    if (buildStatus === 'building') {
      const stall = handleBuildStallCheck(nowMs);
      if (stall === 'fatal') {
        stopEllaRadioBuildPoll();
        return;
      }
    }

    if (buildStatus === 'ready') {
      emitProgress('ready', queuedCount);
      _callbacks.onReady();
      stopEllaRadioBuildPoll();
      return;
    }

    if (buildStatus === 'partial') {
      const msg = formatPartialBuildMessage(queuedCount, _targetCount, blueprint?.buildError);
      emitProgress('partial', queuedCount, msg);
      _callbacks.onPartialFailure(msg);
      stopEllaRadioBuildPoll();
      return;
    }

    if (buildStatus === 'failed') {
      const msg = String(blueprint?.buildError || '').trim()
        || 'Station build failed. Tap retry to start a fresh build.';
      markRadioBuildFatal();
      emitProgress('partial', queuedCount, msg);
      _callbacks.onBuildFailed?.(msg);
      _callbacks.onPartialFailure(msg);
      stopEllaRadioBuildPoll();
      return;
    }

    if (isBuildHeartbeatStale(buildStatus, buildLastProgressAt, nowMs, buildStartedAt)) {
      if (!_stillShapingEmitted) _stillShapingEmitted = true;
      emitProgress(
        'still_shaping',
        queuedCount,
        'Still shaping the set — waiting on the server…',
      );
      schedulePoll(nextPollDelayMs(true));
      return;
    }

    const localDrySpell = queuedCount >= 1 && nowMs - _lastServerTrackIncreaseAt >= UX_PATIENCE_MS;
    if (localDrySpell) {
      if (!_stillShapingEmitted) _stillShapingEmitted = true;
      emitProgress('still_shaping', queuedCount);
      schedulePoll(nextPollDelayMs(true));
      return;
    }

    emitProgress('shaping', queuedCount);
    schedulePoll(nextPollDelayMs(true));
  } catch (e) {
    logRadioPoll('error', 'poll_fetch_failed', {
      playlistId: _playlistId,
      error: String((e as Error)?.message || e),
    });
    markConnectivityIssue();
    schedulePoll(nextPollDelayMs(false));
  } finally {
    _tickInFlight = false;
  }
}

export function stopEllaRadioBuildPoll() {
  if (_pollTimer) clearTimeout(_pollTimer);
  _pollTimer = null;
  _playlistId = null;
  _lastServerQueuedCount = 0;
  _lastServerTrackIncreaseAt = 0;
  _callbacks = null;
  _stillShapingEmitted = false;
  _connectivityActive = false;
  _pollBackoffIndex = 0;
  _tickInFlight = false;
  stopEllaRadioLowQueueWatch();
  releaseRadioConnectorHoldIfIdle();
}

export function isEllaRadioBuildPollActive(): boolean {
  return _pollTimer !== null && _playlistId !== null;
}

export function getEllaRadioBuildPollPlaylistId(): string | null {
  return _playlistId;
}

/** Rebind UI callbacks after RadioErrorBoundary remount — poll timer keeps running module-side. */
export function reattachEllaRadioBuildPollCallbacks(callbacks: EllaBuildPollCallbacks): boolean {
  if (!_pollTimer || !_playlistId) return false;
  _callbacks = callbacks;
  const stage = _connectivityActive
    ? 'connectivity'
    : (_lastServerQueuedCount >= _targetCount ? 'ready' : 'shaping');
  emitProgress(stage, _lastServerQueuedCount);
  return true;
}

export function startEllaRadioBuildPoll(
  playlistId: string,
  targetCount: number,
  initialTrackIds: string[],
  startedAt: number,
  callbacks: EllaBuildPollCallbacks,
) {
  stopEllaRadioBuildPoll();
  _playlistId = playlistId;
  _targetCount = targetCount;
  _startedAt = startedAt;
  _callbacks = callbacks;
  resetRadioSessionPollFlags();
  seedRadioTrackRegistry(initialTrackIds.filter(Boolean));
  syncRadioTrackRegistryFromQueue();
  _lastServerQueuedCount = initialTrackIds.filter(Boolean).length;
  _lastServerTrackIncreaseAt = Date.now();
  _stillShapingEmitted = false;
  _connectivityActive = false;
  _pollBackoffIndex = 0;

  logRadioPoll('info', 'poll_start', { playlistId, targetCount, initialTracks: _lastServerQueuedCount });
  emitProgress('shaping', _lastServerQueuedCount);
  installEllaRadioBuildPollWakeListener();
  if (_lastServerQueuedCount <= 1) {
    armRadioConnectorHold();
  }
  startEllaRadioLowQueueWatch();
  schedulePoll(0);
}

/** Test hook — poll state without starting timers. */
export function __testGetEllaRadioBuildPollState() {
  return {
    playlistId: _playlistId,
    lastServerQueuedCount: _lastServerQueuedCount,
    connectivityActive: _connectivityActive,
    pollBackoffIndex: _pollBackoffIndex,
  };
}

/** One-shot reconcile enqueue — used at radio queue-end before advance/wait. */
export async function flushEllaRadioPendingEnqueue(): Promise<number> {
  if (!_playlistId) return 0;
  try {
    const detail = await smartPlaylistService.getDetail(_playlistId);
    if (!detail?.success) return 0;
    const apiTracks = (detail.tracks || []).map(ellaDetailTrackToApiTrack);
    return reconcileTracksFromServer(apiTracks);
  } catch (e) {
    logRadioPoll('warn', 'flush_enqueue_failed', {
      playlistId: _playlistId,
      error: String((e as Error)?.message || e),
    });
    return 0;
  }
}
